import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import authService from '../api/authService';
import { tokenStore } from '../api/axiosClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokenStore.getUser());
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await authService.me();
      setUser(me);
      tokenStore.set({ user: me });
    } catch {
      // interceptor handles refresh/logout; fall back to stored user
      setUser(tokenStore.getUser());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
    const onLogout = () => setUser(null);
    window.addEventListener('tt:logout', onLogout);
    return () => window.removeEventListener('tt:logout', onLogout);
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const auth = await authService.login(email, password);
    setUser(auth.user);
    return auth.user;
  }, []);

  const registerClient = useCallback(async (payload) => {
    const auth = await authService.registerClient(payload);
    setUser(auth.user);
    return auth.user;
  }, []);

  const registerTechnician = useCallback(async (payload) => {
    const auth = await authService.registerTechnician(payload);
    setUser(auth.user);
    return auth.user;
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      role: user?.role || null,
      login,
      logout,
      registerClient,
      registerTechnician,
      refreshMe,
    }),
    [user, loading, login, logout, registerClient, registerTechnician, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
  return ctx;
}

export default AuthContext;
